import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BookOpen, ArrowUpRight, ScrollText } from 'lucide-react'

import { PageHeader } from '@/components/layout/PageHeader'

/**
 * /help — справочник по разделам админ-панели Стрижа.
 *
 * Гибрид:
 *  - Сверху — карточка-заходник на подробный standalone-справочник
 *    (`public/onboarding/index.html`) с описанием экранов по всем 5 ролям.
 *    Это для новых сотрудников и для отправки клиентам.
 *  - Дальше — короткая шпаргалка с маршрутами и FAQ для своих.
 *
 * Standalone-справочник лежит как статика, открывается в новой вкладке.
 */
export function HelpPage() {
  const { t } = useTranslation()
  return (
    <div>
      <PageHeader
        title={t('help.title')}
        subtitle={t('help.subtitle')}
      />

      <div className="flex flex-col gap-5">
        <a
          href="/onboarding/index.html"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-start gap-4 rounded-lg border border-[color:var(--stryzh-color-accent-orange)]/50 bg-gradient-to-br from-[color:var(--stryzh-color-accent-orange)]/10 to-transparent p-4 transition hover:border-[color:var(--stryzh-color-accent-orange)] hover:from-[color:var(--stryzh-color-accent-orange)]/15"
        >
          <div className="mt-0.5 rounded-md bg-[color:var(--stryzh-color-accent-orange)]/20 p-2 text-[color:var(--stryzh-color-accent-orange)]">
            <BookOpen size={20} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
              {t('help.detailed.title')}
              <ArrowUpRight size={16} className="opacity-60 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
            <div className="mt-1 text-sm text-[color:var(--stryzh-color-text-secondary)]">
              {t('help.detailed.text')}
            </div>
          </div>
        </a>

        <Link
          to="/help/avito"
          className="group flex items-start gap-4 rounded-lg border border-[color:var(--stryzh-color-accent-orange)]/50 bg-gradient-to-br from-[color:var(--stryzh-color-accent-orange)]/10 to-transparent p-4 transition hover:border-[color:var(--stryzh-color-accent-orange)] hover:from-[color:var(--stryzh-color-accent-orange)]/15"
        >
          <div className="mt-0.5 rounded-md bg-[color:var(--stryzh-color-accent-orange)]/20 p-2 text-[color:var(--stryzh-color-accent-orange)]">
            <ScrollText size={20} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
              {t('help.avitoCard.title')}
              <ArrowUpRight size={16} className="opacity-60 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
            <div className="mt-1 text-sm text-[color:var(--stryzh-color-text-secondary)]">
              {t('help.avitoCard.text')}
            </div>
          </div>
        </Link>

        <Section title={t('help.section.ops')}>
          <Item
            to="/orders"
            label={t('help.item.orders.label')}
            text={t('help.item.orders.text')}
          />
          <Item
            to="/dispatch-map"
            label={t('help.item.dispatchMap.label')}
            text={t('help.item.dispatchMap.text')}
          />
          <Item
            to="/inspectors-positions"
            label={t('help.item.inspectorsPositions.label')}
            text={t('help.item.inspectorsPositions.text')}
          />
          <Item to="/map" label={t('help.item.map.label')} text={t('help.item.map.text')} />
        </Section>

        <Section title={t('help.section.directories')}>
          <Item to="/companies" label={t('help.item.companies.label')} text={t('help.item.companies.text')} />
          <Item to="/users" label={t('help.item.users.label')} text={t('help.item.users.text')} />
          <Item to="/tariffs" label={t('help.item.tariffs.label')} text={t('help.item.tariffs.text')} />
        </Section>

        <Section title={t('help.section.finance')}>
          <Item to="/billing" label={t('help.item.billing.label')} text={t('help.item.billing.text')} />
          <Item to="/dkp-generator" label={t('help.item.dkpGenerator.label')} text={t('help.item.dkpGenerator.text')} />
        </Section>

        <Section title={t('help.section.comms')}>
          <Item to="/notifications/log" label={t('help.item.notifLog.label')} text={t('help.item.notifLog.text')} />
          <Item to="/notifications/triggers" label={t('help.item.notifTriggers.label')} text={t('help.item.notifTriggers.text')} />
          <Item to="/notifications/broadcast" label={t('help.item.notifBroadcast.label')} text={t('help.item.notifBroadcast.text')} />
          <Item to="/notifications/subscriptions" label={t('help.item.notifSubscriptions.label')} text={t('help.item.notifSubscriptions.text')} />
        </Section>

        <Section title={t('help.section.system')}>
          <Item to="/system-config" label={t('help.item.systemConfig.label')} text={t('help.item.systemConfig.text')} />
          <Item
            to="/geo-settings"
            label={t('help.item.geo.label')}
            text={t('help.item.geo.text')}
          />
          <Item to="/diagnostics" label={t('help.item.diagnostics.label')} text={t('help.item.diagnostics.text')} />
          <Item to="/audit" label={t('help.item.audit.label')} text={t('help.item.audit.text')} />
          <Item to="/pd-log" label={t('help.item.pdLog.label')} text={t('help.item.pdLog.text')} />
        </Section>

        <Section title={t('help.section.faq')}>
          <Faq
            q={t('help.faq.geoBackground.q')}
            a={t('help.faq.geoBackground.a')}
          />
          <Faq
            q={t('help.faq.testInspector.q')}
            a={t('help.faq.testInspector.a')}
          />
          <Faq
            q={t('help.faq.noPush.q')}
            a={t('help.faq.noPush.a')}
          />
          <Faq
            q={t('help.faq.stuckStatus.q')}
            a={t('help.faq.stuckStatus.a')}
          />
        </Section>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4">
      <div className="mb-3 text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
        {title}
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  )
}

function Item({ to, label, text }: { to: string; label: string; text: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Link
        to={to}
        className="inline-flex w-fit items-baseline gap-1 text-sm font-semibold text-[color:var(--stryzh-color-accent-orange)] hover:underline"
      >
        {label}
        <span className="text-xs opacity-60">↗</span>
      </Link>
      <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">{text}</div>
    </div>
  )
}

function Faq({ q, a }: { q: string; a: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-sm font-semibold text-[color:var(--stryzh-color-text-primary)]">
        {q}
      </div>
      <div className="text-sm text-[color:var(--stryzh-color-text-secondary)]">{a}</div>
    </div>
  )
}
